/* eslint-disable react/style-prop-object,react/no-children-prop */
import React, { PropsWithChildren } from 'react';

import { FormattedNumber } from '@skippay/core/i18n/intl';
import { DEFAULT_CURRENCY } from '@skippay/core/utils/amountUtils';
import { parseMinorUnits } from '@skippay/core/utils/apiUtils';
import { mergeClasses } from '@skippay/core/utils/classUtils';

const { isNaN } = Number;

const prependPlus = (parts) => <span className="colorSuccess">+&nbsp;{parts}</span>;

const DEFAULT_DECIMAL_SEPARATOR = ',';

function formatDecimals(number) {
	return number.length === 1 ? parseInt(number, 10) * 10 : number;
}

interface Props {
	/**
	 * Amount from backend as integer. If passed, will be divided to float value.
	 */
	amount?: number;
	currency?: string;
	maximumFractionDigits?: number;
	minimumFractionDigits?: number;
	/**
	 * The amount is rendered as money transaction, negative amount has no sign, positive amount has plus sign and is
	 * rendered in green color.
	 */
	moneyTransaction?: boolean;
	value?: number | string;
	testId: string;
	innerClassName?: string;
}

function FormattedCurrency(props: PropsWithChildren<Props>): JSX.Element {
	const {
		maximumFractionDigits = 2,
		minimumFractionDigits = 0,
		moneyTransaction,
		testId,
		currency = DEFAULT_CURRENCY,
		innerClassName,
	} = props;
	const rawValue = !isNaN(parseFloat(props.value)) ? props.value : parseMinorUnits(props.amount);
	let formattedValue = rawValue;

	if (moneyTransaction && typeof formattedValue === 'number') {
		formattedValue = Math.abs(formattedValue);
	}

	const CustomFormattedNumber = ({ children, value, ...restProps }) => (
		<FormattedNumber {...restProps} currency={restProps.currency} value={value}>
			{(formattedVal) => {
				const [firstPart, secondPart] = formattedVal.split(DEFAULT_DECIMAL_SEPARATOR);
				return (
					<span
						className={mergeClasses(innerClassName, 'u-nowrap')}
						aria-label={formattedVal}
						data-test-id={testId}
						data-clarity-mask="true"
					>
						{children}
						{firstPart}
						{secondPart && (
							<>
								{DEFAULT_DECIMAL_SEPARATOR}
								<sup className="u-decimals">{formatDecimals(secondPart.split(' ')[0])}</sup>&nbsp;
								{secondPart.split(' ')[1]}
							</>
						)}
					</span>
				);
			}}
		</FormattedNumber>
	);

	return (
		<CustomFormattedNumber
			children={moneyTransaction && rawValue && rawValue > 0 ? prependPlus : undefined}
			currency={currency}
			maximumFractionDigits={maximumFractionDigits}
			minimumFractionDigits={minimumFractionDigits}
			style="currency"
			value={formattedValue}
		/>
	);
}

export default FormattedCurrency;
